import React, { useMemo, useState } from 'react';
import TopBar from '../layout/TopBar';
import Footer from '../layout/Footer';
import BookingModal from '../BookingModal';
import { getFilteredServices } from '../../data/allServicesData';
import { normalizeService, normalizeServiceList } from '../../config/catalog/normalizedServiceSchema';

// Theme styles mapping for safe Tailwind classes
const getThemeClasses = (themeColor) => {
  const themeMap = {
    rose: {
      breadcrumbHover: 'hover:text-rose-600',
      statsIcon: 'text-rose-600',
      searchFocus: 'focus:ring-rose-500',
      filterActive: 'bg-rose-50 text-rose-700 border-rose-200',
      filterActiveCount: 'text-rose-600',
      checkmark: 'text-rose-600',
      button: 'bg-rose-600 hover:bg-rose-700',
      ctaBackground: 'bg-rose-50',
      ctaButton: 'bg-rose-600 hover:bg-rose-700',
      gradient: 'from-rose-50 to-pink-50'
    },
    orange: {
      breadcrumbHover: 'hover:text-orange-600',
      statsIcon: 'text-orange-600',
      searchFocus: 'focus:ring-orange-500',
      filterActive: 'bg-orange-50 text-orange-700 border-orange-200',
      filterActiveCount: 'text-orange-600',
      checkmark: 'text-orange-600',
      button: 'bg-orange-600 hover:bg-orange-700',
      ctaBackground: 'bg-orange-50',
      ctaButton: 'bg-orange-600 hover:bg-orange-700',
      gradient: 'from-orange-50 to-amber-50'
    },
    purple: {
      breadcrumbHover: 'hover:text-purple-600',
      statsIcon: 'text-purple-600',
      searchFocus: 'focus:ring-purple-500',
      filterActive: 'bg-purple-50 text-purple-700 border-purple-200',
      filterActiveCount: 'text-purple-600',
      checkmark: 'text-purple-600',
      button: 'bg-purple-600 hover:bg-purple-700',
      ctaBackground: 'bg-purple-50',
      ctaButton: 'bg-purple-600 hover:bg-purple-700',
      gradient: 'from-purple-50 to-indigo-50'
    },
    green: {
      breadcrumbHover: 'hover:text-green-600',
      statsIcon: 'text-green-600',
      searchFocus: 'focus:ring-green-500',
      filterActive: 'bg-green-50 text-green-700 border-green-200',
      filterActiveCount: 'text-green-600',
      checkmark: 'text-green-600',
      button: 'bg-green-600 hover:bg-green-700',
      ctaBackground: 'bg-green-50',
      ctaButton: 'bg-green-600 hover:bg-green-700',
      gradient: 'from-green-50 to-emerald-50'
    },
    blue: {
      breadcrumbHover: 'hover:text-blue-600',
      statsIcon: 'text-blue-600',
      searchFocus: 'focus:ring-blue-500',
      filterActive: 'bg-blue-50 text-blue-700 border-blue-200',
      filterActiveCount: 'text-blue-600',
      checkmark: 'text-blue-600',
      button: 'bg-blue-600 hover:bg-blue-700',
      ctaBackground: 'bg-blue-50',
      ctaButton: 'bg-blue-600 hover:bg-blue-700',
      gradient: 'from-blue-50 to-cyan-50'
    },
    yellow: {
      breadcrumbHover: 'hover:text-yellow-600',
      statsIcon: 'text-yellow-600',
      searchFocus: 'focus:ring-yellow-500',
      filterActive: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      filterActiveCount: 'text-yellow-600',
      checkmark: 'text-yellow-600',
      button: 'bg-yellow-600 hover:bg-yellow-700',
      ctaBackground: 'bg-yellow-50',
      ctaButton: 'bg-yellow-600 hover:bg-yellow-700',
      gradient: 'from-yellow-50 to-orange-50'
    }
  };
  
  return themeMap[themeColor] || themeMap.blue;
};

const ServicePageTemplate = ({ 
  serviceType,
  services,
  filters,
  pageConfig,
  onNavigate 
}) => {
  const SERVICE_TYPE_TO_PAGE = {
    wedding: 'wedding-services',
    homePooja: 'home-pooja-services',
    festival: 'festival-services',
    lifeEvents: 'life-events',
    spiritual: 'spiritual-services',
    specialOccasions: 'special-occasions'
  };
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const normalizedServices = useMemo(() => normalizeServiceList(services, {
    servicePage: SERVICE_TYPE_TO_PAGE[serviceType] || 'home'
  }), [services, serviceType]);

  const filteredServices = useMemo(() => {
    let filtered = getFilteredServices(serviceType, selectedFilter, normalizedServices);

    if (searchTerm) {
      filtered = filtered.filter(service =>
        service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [selectedFilter, searchTerm, normalizedServices, serviceType]);

  const handleBookNow = (service) => {
    setSelectedService(service);
    setIsBookingModalOpen(true);
  };

  const handleCloseBookingModal = () => {
    setIsBookingModalOpen(false);
    setSelectedService(null);
  };

  const handleConfirmBooking = (bookingData) => {
    if (onNavigate) {
      onNavigate('booking-confirmation', { bookingData });
    }
  };

  const handleLearnMore = (service) => {
    if (onNavigate) {
      const servicePage = SERVICE_TYPE_TO_PAGE[serviceType] || 'home';
      onNavigate('service-details', {
        service: normalizeService(service, { servicePage })
      });
    }
  };

  const { theme, title, subtitle, breadcrumb, stats, trust } = pageConfig;
  const themeClasses = getThemeClasses(theme.primary);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* TopBar */}
      <TopBar 
        onUserClick={() => onNavigate && onNavigate('login')}
        onLogoClick={() => onNavigate && onNavigate('home')}
      />

      {/* Elegant Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-6 sm:mb-8">
            <button
              type="button"
              onClick={() => onNavigate && onNavigate('home')}
              className={`${themeClasses.breadcrumbHover} transition-colors`}
            >
              Home
            </button>
            <span>•</span>
            <span className="text-gray-900 font-medium">{breadcrumb}</span>
          </nav>
          
          {/* Header Content */}
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-light text-gray-900 mb-4 sm:mb-6">
              {title}
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-6 sm:mb-8 px-4 sm:px-0">
              {subtitle}
            </p>
            
            {/* Stats */}
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-8 text-gray-600">
              {stats.map((stat, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <span className={themeClasses.statsIcon}>{stat.icon}</span>
                  <span className="text-sm sm:text-base">{stat.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Sidebar Filters */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 sticky top-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter Services</h3>
              
              {/* Search */}
              <div className="mb-6">
                <input
                  type="text"
                  placeholder={`Search ${breadcrumb.toLowerCase()}...`}
                  className={`w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg ${themeClasses.searchFocus} focus:border-transparent text-sm sm:text-base`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Categories */}
              <div className="space-y-2">
                {filters.map(category => (
                  <button
                    type="button"
                    key={category.id}
                    onClick={() => setSelectedFilter(category.id)}
                    className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 rounded-lg transition-colors text-sm sm:text-base ${
                      selectedFilter === category.id
                        ? `${themeClasses.filterActive} border`
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span>{category.name}</span>
                      <span className={`text-xs sm:text-sm ${
                        selectedFilter === category.id ? themeClasses.filterActiveCount : 'text-gray-500'
                      }`}>
                        {category.count}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Services Grid */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              {filteredServices.map(service => (
                <div key={service.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                  {/* Service Image/Icon */}
                  <div className={`bg-gradient-to-br ${themeClasses.gradient} p-6 sm:p-8 text-center`}>
                    <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">{service.image}</div>
                    <div className="flex items-center justify-center space-x-2 text-xs sm:text-sm text-gray-600">
                      <span className="flex items-center space-x-1">
                        <span className="text-yellow-500">⭐</span>
                        <span>{service.rating}</span>
                      </span>
                      <span>•</span>
                      <span>{service.reviews} reviews</span>
                    </div>
                  </div>

                  {/* Service Details */}
                  <div className="p-4 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600 mb-4 text-sm leading-relaxed">{service.description}</p>
                    
                    {/* Features */}
                    <div className="space-y-2 mb-4">
                      {service.features.slice(0, 3).map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2 text-xs sm:text-sm text-gray-600">
                          <span className={themeClasses.checkmark}>✓</span>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Price and Duration */}
                    <div className="flex justify-between items-center mb-4 py-3 border-t border-gray-100">
                      <div>
                        <div className="text-xl sm:text-2xl font-bold text-gray-900">{service.price}</div>
                        <div className="text-xs sm:text-sm text-gray-500">{service.duration}</div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      <button
                        type="button"
                        onClick={() => handleLearnMore(service)}
                        className="px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm font-medium"
                      >
                        Learn More
                      </button>
                      <button
                        type="button"
                        onClick={() => handleBookNow(service)}
                        className={`px-3 sm:px-4 py-2 ${themeClasses.button} text-white rounded-lg transition-colors text-xs sm:text-sm font-medium`}
                      >
                        Book Now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* No Results */}
            {filteredServices.length === 0 && (
              <div className="text-center py-8 sm:py-12 col-span-full">
                <div className="text-4xl sm:text-6xl mb-4">🔍</div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No services found</h3>
                <p className="text-sm sm:text-base text-gray-600">Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom Service CTA */}
      <div className={`${themeClasses.ctaBackground} py-8 sm:py-12`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-3xl sm:text-4xl mb-4">✨</div>
          <h2 className="text-2xl sm:text-3xl font-light text-gray-900 mb-4">
            Need Something Special?
          </h2>
          <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
            Have unique requirements for your {breadcrumb.toLowerCase()}? Our experts can create a completely customized service tailored to your traditions and preferences.
          </p>
          <button
            type="button"
            onClick={() => onNavigate && onNavigate('request-custom-service')}
            className={`inline-flex items-center px-8 py-3 ${themeClasses.ctaButton} text-white font-medium rounded-lg transition-colors`}
          >
            <span className="mr-2">💎</span>
            Request Custom Service
          </button>
        </div>
      </div>

      {/* Trust Section */}
      <div className="bg-white border-t border-gray-200 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 text-center">
            {trust.map((item, index) => (
              <div key={index} className="p-4">
                <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">{item.icon}</div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer onNavigate={onNavigate} />

      {/* Booking Modal */}
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={handleCloseBookingModal}
        service={selectedService}
        onConfirmBooking={handleConfirmBooking}
      />
    </div>
  );
};

export default ServicePageTemplate;
